<template>
	<BaseLayout
		:footer-full="true"
		:include-padding="false"
		:transparent-header="true"
		background-color-class="bg-color-content-primary"
	>
		<BasePlaceHolder
			:line-count="10"
			:class="{ 'container marketing-section': isLoading }"
			:placehold="isLoading"
			theme="dark"
		>
			<OcfHero :hero="hero" :fund-structure-details="fundStructureDetails" />
			<OcfFundFeatures :cards="fundFeaturesCards" class="bg-color-white px-100 px-150-md" />
			<OcfWhyPrivateCredit />
			<AboutOurStrategySection
				heading="Market dislocation creates opportunity"
				:example-projects="exampleProjects"
			/>
			<OurTrackRecordSection />
			<OurBankAndRealEstatePartnersSection class="pt-600-md pt-300-smo" />
			<OcfProperties :fund="fundDetails" :assets="assetElements" />
			<CtaSection :benefits-list="[]" theme="dark">
				<template #cta>
					<LeadCaptureOcf :config="ocfLeadCaptureConfig" component-id="header-component" class="mt-300-md" />
				</template>
			</CtaSection>
		</BasePlaceHolder>
	</BaseLayout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import AboutOurStrategySection from '@components/marketing-site/private-credit/about-our-strategy-section.vue';
import { Asset } from 'types/assets';
import CtaSection from '@components/marketing-site/cta-section.vue';
import { exampleProjects } from '@constants/about-our-strategy';
import { Fund } from 'types/funds';
import { getAssets } from '@api/sanity/proxy/assets';
import { getFund } from '@api/sanity/proxy/funds';
import { KeyValue } from 'types/layout';
import LeadCaptureOcf from '@components/lead-capture/lead-capture-ocf.vue';
import { logError } from '@utils/error-tracking';
import { OcfConfig } from 'types/layout/lead';
import OcfFundFeatures from '@components/marketing-site/opportunistic-credit-fund/ocf-fund-features.vue';
import OcfHero from '@components/marketing-site/opportunistic-credit-fund/ocf-hero.vue';
import { ocfLeadCaptureKey } from '@constants/marketing-site';
import OcfProperties from '@components/marketing-site/opportunistic-credit-fund/ocf-properties.vue';
import { ocfReitId } from '@constants/reit';
import OcfWhyPrivateCredit from '@components/marketing-site/opportunistic-credit-fund/ocf-why-private-credit.vue';
import OurBankAndRealEstatePartnersSection from '@components/marketing-site/private-credit/our-banking-and-real-estate-partners-section.vue';
import OurTrackRecordSection from '@components/marketing-site/private-credit/our-track-record-section.vue';
import { useHead } from '@unhead/vue';

const isLoading = ref(true);

const assetElements = ref<Array<Asset>>([]);
const fundDetails = ref<Fund | null>(null);

const ocfLeadCaptureConfig: OcfConfig = {
	localStorageLeadCaptureKey: ocfLeadCaptureKey,
	waitlistRouteName: 'campaign-ocf-join-waitlist',
	loginRedirectPath: '/campaigns/opportunistic-credit-fund/join-waitlist'
};

const hero = {
	heading: 'Opportunistic Credit Fund',
	subheading: 'A private lending fund calibrated for today’s market',
	description:
		'Income-focused investors can now directly take advantage of the significant dislocation in real estate credit markets.',
	leadCaptureConfig: ocfLeadCaptureConfig
};

const fundFeaturesCards = [
	{
		title: 'Targeting a once-in-a-decade lending environment',
		description:
			'Rapidly rising interest rates and a potential liquidity crisis creates a rare and advantageous environment for well-capitalized private credit investors.'
	},
	{
		title: 'Concentrating within high-growth markets',
		description:
			'The fund will primarily target fast-growing Sunbelt markets like Dallas-Fort Worth, Atlanta, Phoenix, Orlando, Tampa, and Charleston.'
	},
	{
		title: 'Focusing on high-quality assets with creditworthy borrowers',
		description:
			'The fund aims to provide rescue capital to high-end borrowers experiencing circumstantial liquidity needs.'
	},
	{
		title: 'Leveraging Fundrise’s billion-dollar experience and relationships',
		description:
			'Fundrise has acquired or financed 37,000+ residential units and made more than 71 unique mezzanine and preferred equity investments, collectively worth over $7 billion.'
	}
];

const fundStructureDetails = computed(
	(): Array<KeyValue> => [
		{
			key: 'Current Dividend',
			value: fundDetails.value?.approxAnnualizedDividendPercentage
				? `${fundDetails.value.approxAnnualizedDividendPercentage}%`
				: 'N/A'
		},
		{
			key: 'Term',
			value: '5-7 years'
		},
		{
			key: 'Minimum Initial Investment',
			value: '$100,000'
		},
		{
			key: 'Distribution Frequency',
			value: 'Quarterly'
		},
		{
			key: 'Liquidity',
			value: 'None'
		},
		{
			key: 'Asset Management Fee',
			value: '1.75%'
		},
		{
			key: 'Management Incentive Fee',
			value: '20% over 10% <br> preferred return'
		}
	]
);

const metaTitle = 'Opportunistic Credit Fund';
const metaDescription =
	"Income-focused investors can take advantage of the dislocation in real estate debt with this private credit fund calibrated for today's market.";

async function setup(): Promise<void> {
	useHead({
		title: metaTitle,
		meta: [
			{ property: 'og:title', content: metaTitle },
			{ name: 'description', content: metaDescription },
			{ property: 'og:description', content: metaDescription }
		]
	});

	try {
		const [fundDetailsResponse, assetsResponse] = await Promise.all([
			getFund(ocfReitId),
			getAssets({ fundId: [ocfReitId], status: 'ACTIVE' })
		]);

		fundDetails.value = fundDetailsResponse ?? null;
		assetElements.value = assetsResponse.results;
	} catch {
		logError('Error fetching fund details on OCF page', 'info');
	} finally {
		isLoading.value = false;
	}
}

setup();
</script>
